import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import SelectionTableViewDialog from "@components/smart/selectionTableView/SelectionTableViewDialog";
import { ActionType, addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { getExpandedEnumProps } from "@odata/Data.utils";
import {
    EntityTypeName,
    IPrBenefitEntity,
    IPrEmploymentEntity,
    IPrEmploymentExtraSalaryEntity,
    IPrEmploymentTemplateEntity,
    IPrEmploymentTemplateExtraSalaryEntity,
    IPrEmploymentTemplateSalaryComponentEntity,
    IPrExtraSalaryEntity,
    IPrSalaryComponentEntity,
    PrEmploymentBenefitEntity,
    PrEmploymentEntity,
    PrEmploymentRetirementSavingEntity,
    PrEmploymentSalaryComponentEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemporalEntity,
    PrStatisticalDataEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrEmploymentTypeCode,
    PrExtraSalaryOriginCode,
    PrExtraSalaryTypeCode,
    PrSalaryComponentTypeCode,
    PrSubmissionTypeCode
} from "@odata/GeneratedEnums";
import {
    expandCurrentTemporalBagFromDefault,
    handleCurrentTemporalPropChange,
    handleTemporalLineItemsActions,
    TemporalEntityProp,
    TTemporalLineItem
} from "@odata/TemporalUtils";
import { getDefinitionsFactory as getPickBenefitDefinitionsFactory } from "@pages/payroll/benefits/Benefit.def";
import { recalculateBenefitMealVoucherAmounts } from "@pages/payroll/benefits/Benefit.utils";
import PickBenefitTableView from "@pages/payroll/benefits/PickBenefitTableView";
import { handleRetirementSavingsLineItemsChange } from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import { isObjectEmpty } from "@utils/general";
import { cloneDeep } from "lodash";
import React from "react";

import { FormMode } from "../../../enums";
import BindingContext, { createPath, IEntity } from "../../../odata/BindingContext";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import { loadAndStoreRanges, setMatchingNumberRange } from "../../numberRange/NumberRange.utils";
import { IGetDefinition } from "../../PageUtils";
import {
    createDefaultLegislativeExtraSalaryItems,
    createNewBenefitItemFromDefault,
    createNewExtraSalaryItemFromDefault,
    createNewSalaryComponentItemFromDefault,
    DialogType,
    ExtraSalaryTogglePath,
    initWithPayrollSettings,
    isLegislativeExtraSalaryItem,
    LegislativeExtraSalaryLocalPath,
    onBeforeSave,
    predefineIsceProperty,
    TEmploymentLineItemProp
} from "../employment/Employment.utils";
import { getDefinitionsFactory as getPickExtraSalaryDefinitionsFactory } from "../extraSalary/ExtraSalaryDef";
import PickExtraSalaryTableView from "../extraSalary/PickExtraSalaryTableView";
import PickSalaryComponentTableView from "../salaryComponent/PickSalaryComponentTableView";
import { getDefinitionsFactory as getPickSalaryComponentDefinitionsFactory } from "../salaryComponent/SalaryComponentDef";


type IEmploymentBaseEntity = IPrEmploymentTemplateEntity | IPrEmploymentEntity;

export interface IEmploymentFormCustomData extends IFormStorageDefaultCustomData {
    submissionType?: PrSubmissionTypeCode;
    isEndingEmployment?: boolean;
}

interface IProps extends IFormViewProps<IEmploymentBaseEntity, IEmploymentFormCustomData> {
}

abstract class EmploymentBaseFormView<Props extends IProps> extends FormViewForExtend<IEmploymentBaseEntity, Props> {
    _dialogType: DialogType = null;

    constructor(props: Props) {
        super(props);

        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.onAfterLoad = this.onAfterLoad.bind(this);
        this.handleLineItemsChange = this.handleLineItemsChange.bind(this);
        this.handleExtraSalarySelection = this.handleExtraSalarySelection.bind(this);
        this.handleSalaryComponentSelection = this.handleSalaryComponentSelection.bind(this);
        this.handleBenefitSelection = this.handleBenefitSelection.bind(this);
    }

    get isDppOrDpc(): boolean {
        const { storage } = this.props;
        if (!storage.isValidPath(PrEmploymentEntity.TypeCode)) {
            return false;
        }
        const typeCode = storage.getValueByPath(PrEmploymentEntity.TypeCode);
        return typeCode === PrEmploymentTypeCode.WorkAgreementDPP || typeCode === PrEmploymentTypeCode.WorkAgreementDPC;
    }

    async onAfterLoad(): Promise<void> {
        const { storage } = this.props;
        const isAuditTrail = storage.formMode === FormMode.AuditTrail;
        const isNew = storage.data.bindingContext.isNew();


        if (!isAuditTrail) {
            const promises: Promise<unknown>[] = [];

            if (!storage.data.entity.NumberOurs) {
                promises.push(setMatchingNumberRange(storage, true));
            } else {
                promises.push(loadAndStoreRanges(storage));
            }

            storage.setValueByPath(LegislativeExtraSalaryLocalPath, false);
            const customExtraSalary = storage.getValueByPath(PrEmploymentEntity.ExtraSalaries)?.find((item: IPrEmploymentExtraSalaryEntity) =>
                    !isLegislativeExtraSalaryItem(item));
            storage.setValueByPath(ExtraSalaryTogglePath, !!customExtraSalary);

            if (isNew) {
                promises.push(createDefaultLegislativeExtraSalaryItems(storage));
                promises.push(initWithPayrollSettings(storage));
                predefineIsceProperty(storage, storage.getValueByPath(PrEmploymentEntity.TypeCode), storage.getValueByPath(PrEmploymentEntity.DateEnd), createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.Isce));
            } else {
                this.expandCurrentTemporalBagsFromDefault();
                this.copyCollectionValuesFromDefault();
            }
            await Promise.all(promises);
        }

        return super.onAfterLoad();
    }

    expandCurrentTemporalBagsFromDefault(): void {
        const { storage } = this.props;
        expandCurrentTemporalBagFromDefault(storage, PrEmploymentEntity.ExtraSalaries);
        expandCurrentTemporalBagFromDefault(storage, PrEmploymentEntity.SalaryComponents);
        expandCurrentTemporalBagFromDefault(storage, PrEmploymentEntity.Benefits);
    }

    copyCollectionValuesFromDefault(): void {
        const { storage } = this.props;
        storage.data.entity.Benefits?.forEach(benefit => {
            if (!isObjectEmpty(benefit?.Default)) {
                const props = getExpandedEnumProps(EntityTypeName.PrBenefitType, benefit.Default.TypeCode, PrEmploymentBenefitEntity.Type);
                benefit[PrEmploymentBenefitEntity.Type] = props.Type;
                benefit[PrEmploymentBenefitEntity.TypeCode] = props.TypeCode;
            }
        });
        storage.data.entity.SalaryComponents?.forEach(sc => {
            if (!isObjectEmpty(sc?.Default)) {
                const props = getExpandedEnumProps(EntityTypeName.PrSalaryComponentType, sc.Default.TypeCode, PrEmploymentSalaryComponentEntity.Type);
                sc[PrEmploymentSalaryComponentEntity.Type] = props.Type;
                sc[PrEmploymentSalaryComponentEntity.TypeCode] = props.TypeCode;

                const regProps = getExpandedEnumProps(EntityTypeName.PrRegularSalaryType, sc.Default.RegularSalaryTypeCode, PrEmploymentSalaryComponentEntity.RegularSalaryType);
                sc[PrEmploymentSalaryComponentEntity.RegularSalaryType] = regProps.RegularSalaryType;
                sc[PrEmploymentSalaryComponentEntity.RegularSalaryTypeCode] = regProps.RegularSalaryTypeCode;
            }
        });
    }

    handleSalaryComponentTypeChange(e: ISmartFieldChange, lineItemBc: BindingContext): void {
        const { storage } = this.props;
        if (e.value === PrSalaryComponentTypeCode.Regular) {
            storage.setDefaultValue(lineItemBc.navigate(PrEmploymentSalaryComponentEntity.RegularSalaryType));
            return;
        }
        storage.clearValue(lineItemBc.navigate(PrEmploymentSalaryComponentEntity.RegularSalaryType));
        storage.clearValue(lineItemBc.navigate(PrEmploymentSalaryComponentEntity.RegularSalaryTypeCode));
    }

    handleNumberRangeChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === "NumberRange") {
            this.props.storage.data.entity.NumberRange = e.value ? e.additionalData : null;
        }
    };

    handleWorkingPatternChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrEmploymentTemporalEntity.WorkingPattern && e.triggerAdditionalTasks) {
            this.props.storage.refresh();
        }
    };

    handleIsceDefaulting = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        const isTypeChange = e.bindingContext.getPath() === PrEmploymentEntity.Type && e.triggerAdditionalTasks;
        const isDateEndChange = e.bindingContext.getPath() === PrEmploymentEntity.DateEnd;
        if (isTypeChange || isDateEndChange) {
            const typeCode = storage.getValueByPath(PrEmploymentEntity.TypeCode) as PrEmploymentTypeCode;
            const dateEnd = storage.getValueByPath(PrEmploymentEntity.DateEnd) as Date;
            predefineIsceProperty(storage, typeCode, dateEnd, createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.Isce));
        }
    }

    handleDateStartChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() !== PrEmploymentEntity.DateStart) {
            return;
        }
        const { storage } = this.props;
        const { bindingContext } = storage.data;
        const dateStart = e.value as Date;
        if (bindingContext.isNew() && !storage.isDirty(bindingContext.navigate(PrEmploymentEntity.DateCalculatePayslipStart))) {
            storage.setValueByPath(PrEmploymentEntity.DateCalculatePayslipStart, getUtcDayjs(dateStart).startOf("month").toDate());
        }
    }

    handleChange(e: ISmartFieldChange): void {
        this.props.storage.handleChange(e);
        handleCurrentTemporalPropChange(e, this.props.storage);

        this.handleDateStartChange(e);
        this.handleNumberRangeChange(e);


        const shouldUpdate = e.triggerAdditionalTasks !== false;
        this.props.storage.refreshFields(shouldUpdate);

        this.handleIsceDefaulting(e);
        this.handleWorkingPatternChange(e);
    }

    handleLineItemsChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        storage.handleLineItemsChange(e);

        const path = e.bindingContext.getPath();
        const parent = e.bindingContext.getParent();
        const isCurrentPropertyBag = parent.getPath() === TemporalEntityProp.CurrentTemporalPropertyBag;
        const collectionBc = e.bindingContext.getParentCollection();
        const collectionPath = collectionBc?.getPath(true);

        const lineItemBc = isCurrentPropertyBag ? parent.getParent() : parent;

        if (lineItemBc.isValidNavigation(TemporalEntityProp.TemporalPropertyBag)) {
            const lineItemTemporalBagBc = lineItemBc.navigate(TemporalEntityProp.TemporalPropertyBag);
            const temporalBagPath = lineItemTemporalBagBc.getNavigationPath(false, lineItemTemporalBagBc);

            if (isCurrentPropertyBag) {
                // change only current value
                handleCurrentTemporalPropChange(e, storage, temporalBagPath);
            }
        }
        const isSalaryComponentType = (collectionPath === PrEmploymentEntity.SalaryComponents
                || collectionPath === PrEmploymentTemplateEntity.SalaryComponents)
            && path === PrEmploymentSalaryComponentEntity.Type;
        const isBenefitType = (collectionPath === PrEmploymentEntity.Benefits
                || collectionPath === PrEmploymentTemplateEntity.Benefits)
            && path === PrEmploymentBenefitEntity.Type;

        if (isSalaryComponentType) {
            this.handleSalaryComponentTypeChange(e, lineItemBc);
        }

        if (isSalaryComponentType || isBenefitType) {
            // refresh whole group to show fields based on the selected type
            storage.addActiveGroup(e.bindingContext);
        }

        handleRetirementSavingsLineItemsChange(e, this.props.storage as FormStorage<any>);
        if (collectionPath === PrEmploymentEntity.RetirementSavings) {
            const lineItemTemporalBagBc = collectionBc.navigate(PrEmploymentRetirementSavingEntity.TemporalPropertyBag);
            const temporalBagPath = lineItemTemporalBagBc.getNavigationPath(false, lineItemTemporalBagBc);
            handleCurrentTemporalPropChange(e, storage, temporalBagPath);
        }

        const shouldUpdate = e.triggerAdditionalTasks !== false;
        storage.refreshFields(shouldUpdate);
    }

    handleSmartTemporalPropertyDialogChange(args: ISmartFieldChange): void {
        if (args.bindingContext.getPath() === PrEmploymentTemporalEntity.WorkingPattern) {
            this.props.storage.addActiveField(args.bindingContext.getParent().navigate(PrEmploymentTemporalEntity.LeaveDays));
        }
    }

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent<IPrEmploymentTemplateExtraSalaryEntity | IPrEmploymentTemplateSalaryComponentEntity>): void => {
        const { storage } = this.props;

        if (args.actionType === ActionType.Custom) {
            switch (args.customActionType) {
                case DialogType.SalaryComponent:
                case DialogType.ExtraSalary:
                case DialogType.Benefit:
                    // const { storage } = this.props;
                    this._dialogType = args.customActionType as DialogType;
                    break;

                default:
                    throw new Error(`Unknown custom actionType ${args.customActionType}`);
            }
            this.forceUpdate();
        } else {
            handleTemporalLineItemsActions(storage, args as ISmartFastEntriesActionEvent<TTemporalLineItem>);
        }
    };

    async handleExtraSalarySelection(selected: BindingContext[], entities: IPrExtraSalaryEntity[]): Promise<void> {
        const { storage } = this.props;

        entities.forEach((entity, idx) => {
            const newExtraSalary = createNewExtraSalaryItemFromDefault(entity);
            addNewLineItem(storage, PrEmploymentTemplateEntity.ExtraSalaries, PrEmploymentTemplateEntity.ExtraSalaries, [], newExtraSalary);
        });

        storage.refresh();
    }

    async handleSalaryComponentSelection(selected: BindingContext[], entities: IPrSalaryComponentEntity[]): Promise<void> {
        const { storage } = this.props;

        entities.forEach((entity: IPrSalaryComponentEntity, idx) => {
            const newSalaryComponent = createNewSalaryComponentItemFromDefault(entity);
            addNewLineItem(storage, PrEmploymentTemplateEntity.SalaryComponents, PrEmploymentTemplateEntity.SalaryComponents, [], newSalaryComponent);
        });

        storage.refresh();
    }

    async handleBenefitSelection(selected: BindingContext[], entities: IPrBenefitEntity[]): Promise<void> {
        const { storage } = this.props;

        entities.forEach((entity: IPrBenefitEntity, idx) => {
            const newBenefit = createNewBenefitItemFromDefault(entity);
            addNewLineItem(storage, PrEmploymentTemplateEntity.Benefits, PrEmploymentTemplateEntity.Benefits, [], newBenefit);
        });

        storage.refresh();
    }

    handleCloseDialog = (): void => {
        this._dialogType = null;
        this.forceUpdate();
    };

    handleSmartTemporalPropertyDialogBlur(args: ISmartFieldBlur): void {
        recalculateBenefitMealVoucherAmounts(this.props.storage, args);
    }

    handleBlur = async (args: ISmartFieldBlur): Promise<void> => {
        if (args.wasChanged) {
            recalculateBenefitMealVoucherAmounts(this.props.storage, args);
        }
        super.handleBlur(args);
    }

    abstract getHeaderData: () => IReadOnlyListItem[];

    abstract get readOnlyListTitle(): string;


    onBeforeSave(): IEntity {
        const { storage } = this.props;
        const entity = cloneDeep(storage.data.entity);

        if (this.isDppOrDpc) {
            entity.ExtraSalaries = entity.ExtraSalaries.filter(es => {
                return !(es.Default.Type.OriginCode === PrExtraSalaryOriginCode.Legislative && es.Default.Type?.Code === PrExtraSalaryTypeCode.Overtime);
            });
        }

        return onBeforeSave(storage, entity);
    }

    getAlreadySelectedItemIds(propName: TEmploymentLineItemProp): number[] {
        const { entity } = this.props.storage.data;
        const ids: number[] = [];
        entity[propName]?.forEach(comp => {
            if (comp?.Default?.Id) {
                ids.push(comp?.Default?.Id);
            }
        });
        return ids;
    }

    renderDialog(): React.ReactElement {
        const { storage } = this.props;
        let tableView;
        let getDef: IGetDefinition;
        let onSelection: (selected: BindingContext[], entities: IEntity[]) => Promise<void>;
        const isCompensation = (this.entity as IPrEmploymentEntity)?.TypeCode === PrEmploymentTypeCode.Compensation;
        // templates don't have type code
        const isDpp = storage.isValidPath(PrEmploymentEntity.TypeCode) && storage.getValueByPath(PrEmploymentEntity.TypeCode) === PrEmploymentTypeCode.WorkAgreementDPP;

        switch (this._dialogType) {
            case DialogType.SalaryComponent:
                tableView = PickSalaryComponentTableView;
                getDef = getPickSalaryComponentDefinitionsFactory(true, [], isCompensation, isDpp); // this.getAlreadySelectedItemIds(PrEmploymentEntity.SalaryComponents)
                onSelection = this.handleSalaryComponentSelection;
                break;

            case DialogType.ExtraSalary:
                tableView = PickExtraSalaryTableView;
                getDef = getPickExtraSalaryDefinitionsFactory({
                    inEditableWindow: true,
                    withoutLegallyRequired: true,
                    selectedIds: [] // this.getAlreadySelectedItemIds(PrEmploymentEntity.ExtraSalaries)
                });
                onSelection = this.handleExtraSalarySelection;
                break;

            case DialogType.Benefit:
                tableView = PickBenefitTableView;
                getDef = getPickBenefitDefinitionsFactory({
                    inEditableWindow: true,
                    selectedIds: [] // this.getAlreadySelectedItemIds(PrEmploymentEntity.Benefits)
                });
                onSelection = this.handleBenefitSelection;
                break;

            default:
                // possible wrong configuration or no dialog configured
                return null;
        }
        return (
                <SelectionTableViewDialog storage={storage}
                                          tableView={tableView}
                                          getDefinition={getDef}
                                          title={this.readOnlyListTitle}
                                          headerData={this.getHeaderData()}
                                          onSelection={onSelection}
                                          onClose={this.handleCloseDialog}
                                          isSingleSelect={false}/>
        );
    }

    render() {
        const content = super.render();

        return (<>
            {content}
            {this.renderDialog()}
        </>);
    }
}

export default EmploymentBaseFormView;
